import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { translateZoneName } from '@medical/shared/utils/translateZoneName';
import BodyGridFront from '../assets/body-grid front.svg';
import BodyGridBack from '../assets/body-grid back.svg';

const DiagramContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 600px;
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  margin: 2rem 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1rem 0;
    min-height: 400px;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    margin: 0.5rem 0;
    min-height: 350px;
  }
`;

const SVGContainer = styled.div`
  width: 100%;
  max-width: 400px;
  height: auto;
  position: relative;
  aspect-ratio: ${props => props.view === 'back' ? '369 / 1008' : '366 / 1008'};

  @media (max-width: 768px) {
    max-width: 300px;
  }

  @media (max-width: 480px) {
    max-width: 200px;
  }
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const ClickOverlay = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: all;
  overflow: visible;
  
  path {
    fill: transparent !important;
    stroke: transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      fill: rgba(59, 130, 246, 0.1) !important;
      stroke: #3b82f6;
      stroke-width: 2;
    }
    
    &.selected {
      fill: rgba(255, 107, 53, 0.2) !important;
      stroke: #ff6b35;
      stroke-width: 3;
    }
  }
`;

const ViewIndicator = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(102, 126, 234, 0.95);
  color: white;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 10;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
  backdrop-filter: blur(4px);

  @media (max-width: 768px) {
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.35rem 0.65rem;
    font-size: 0.7rem;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    top: 0.5rem;
    right: 0.5rem;
    padding: 0.3rem 0.55rem;
    font-size: 0.65rem;
    border-radius: 5px;
  }
`;

// Zone names for front view (48 zones) - Top to bottom, row-wise
const FRONT_ZONE_NAMES = [
  'right palm',
  'left lower abdomen',
  'right lower abdomen',
  'left upper abdomen',
  'right upper abdomen',
  'left front forearm',
  'left elbow',
  'right elbow',
  'front head right',
  'front head left',
  'frons right',
  'frons left',
  'left shoulder',
  'cervicis left',
  'left lower leg',
  'right lower leg',
  'left ankle',
  'right ankle',
  'right foot',
  'left foot',
  'left upper arm',
  'left lower arm',
  'facies left',
  'cervicis right',
  'facies right',
  'right front forearm',
  'right wrist',
  'left wrist',
  'left palm',
  'right upper chest',
  'left upper chest',
  'right lower chest',
  'left lower chest',
  'right shoulder',
  'right upper arm',
  'right diaphragm',
  'right lower arm',
  'left diaphragm',
  'left upper leg',
  'left knee',
  'left lower thigh',
  'left upper thigh',
  'right upper leg',
  'right knee',
  'right lower thigh',
  'right upper thigh',
  'right pelvic region',
  'left pelvic region'
];

// Zone names for back view (42 zones) - Top to bottom, row-wise (duplicates removed)
const BACK_ZONE_NAMES = [
  'left palm',
  'right palm',
  'back head left',
  'back head right',
  'left upper back',
  'left lumbar',
  'left buttock',
  'left upper thigh',
  'right upper thigh',
  'left lower thigh',
  'right lower thigh',
  'left back of knee',
  'right back of knee',
  'left upper calf',
  'right upper calf',
  'right lower calf',
  'left lower calf',
  'left ankle & foot',
  'right upper forearm',
  'left lower arm',
  'left upper forearm',
  'right ankle & foot',
  'left shoulder',
  'right shoulder',
  'right buttock',
  'lower head left',
  'lower head right',
  'neck left',
  'right upper back',
  'left upper arm',
  'left middle back',
  'right middle back',
  'right upper arm',
  'right lower arm',
  'left lower forearm',
  'right lumbar',
  'left sacral',
  'right sacral',
  'right lower forearm',
  'right wrist',
  'left wrist',
  'neck right'
];

// Generate zone mapping based on path index
const generateZoneMap = (view, pathCount, t) => {
  const zones = {};
  const nameArray = view === 'front' ? FRONT_ZONE_NAMES : BACK_ZONE_NAMES;
  for (let i = 1; i <= pathCount; i++) {
    const zoneId = `${view}-zone-${i}`;
    const rawName = nameArray[i - 1] || `${view === 'front' ? 'Front' : 'Back'} Zone ${i}`;
    zones[`${view}-${i}`] = { 
      zoneId, 
      name: translateZoneName(rawName, t)
    };
  }
  return zones;
};

// These will be generated with translations when component mounts
let FRONT_ZONE_MAP = {};
let BACK_ZONE_MAP = {};

const FRONT_PATHS = [
  { d: "M0 526H40L44.5 534.5L45 537V545.5L44 557L41 571L38 574H35.5L31 567L29 561L27 553L26 548H24L23 550V561L29 574L42 591V594L40 596H29L26 595L19 591C17.0963 589.389 13.5973 586.44 13.0472 586.019C13.049 586.039 13.034 586.034 13 586C12.9514 585.951 12.9712 585.961 13.0472 586.019C13.0279 585.806 11.0675 582.677 10 581L5 569.5L2 558.5L1 552L0 544V526Z" },
  { d: "M184 417H264.136L270 478H184V417Z" },
  { d: "M98.9266 416.999L184 416.999L184 478L94.0001 478L98.9266 416.999Z" },
  { d: "M184 354H264V417H184V354Z" },
  { d: "M99 354H184V417H99V354Z" },
  { d: "M301 420H359L362 479H349H346H320L301 420Z" },
  { d: "M288 357H351L359 420H301L288 357Z" },
  { d: "M75 356H14L5 419H62L75 356Z" },
  { d: "M130 46C130 34.0653 137.061 17.9392 147 9.50005C156.939 1.06091 173 -3.19123e-10 184 0V46H130Z" },
  { d: "M235 46C233.5 38.5 231.189 24.9391 222 16.5C212.811 8.06087 198 -3.52412e-06 184 0V46H235Z" },
  { d: "M130 46H184V81H130V46Z" },
  { d: "M184 46H235V81H184V46Z" },
  { d: "M327 238.021C327 230.473 324.391 213.23 321.955 206.257C319.518 199.284 320.901 199.828 316.399 194.491C311.897 189.154 300.937 182.294 295.054 179.406C289.172 176.517 282.367 175 276 175V238.021H327Z" },
  { d: "M184 135L217 127L227.5 154.868L245.5 170.647L277 175H184V135Z" },
  { d: "M204 815H213H277L268 891H216L204 815Z" },
  { d: "M86 815H160.5L148 891L95 891.5L86 815Z" },
  { d: "M216 891H268L269 960H257H247H239H231.5H221L216 891Z" },
  { d: "M95 891H148L143 958H98H95V891Z" },
  { d: "M98.5 958H143L144.5 963.5L145.245 970V990.176L143.5 994.923L142 997.56L140.5 1000.73L138 1002.84L123.5 1006H98.5L91 1004.42L85.5 1001.25L83.5 1000L82 998L81 994.5L80 988.632L81 982.5L86.5 970.5L92 963.275L95 958H98.5Z" },
  { d: "M241 960H221L219 968L218 974.5V981V987.5L221 994L223 998L225 1000L227 1002L228 1003.13L238 1008H250H261.5L269 1006H270.5L273 1005L275.379 1004H276.5H278.5L283 1003.13L284 1002L286 1000V996.5V993L284 987.5L282.5 984.5L281 981L279 976.5L276.968 973.5L274.573 969L272 965.816L271 962L269 960H254.5H241Z" },
  { d: "M276 238H327L337 292H276L276 238Z" },
  { d: "M276 292H337L351 357H288L276 292Z" },
  { d: "M184 81H235L217 127L184 135V81Z" },
  { d: "M146 127L184 135V175H85L124 164.5L141 146.5L146 127Z" },
  { d: "M130 81H184V135L146 127L130 81Z" },
  { d: "M5 419H62L45 480H1L5 419Z" },
  { d: "M0.999994 480H45L35 500V512L40 526H3.8147e-06L0.999994 480Z" },
  { d: "M320 479H362L366 522H325L326.5 507.5L325 485.5L320 479Z" },
  { d: "M366 522H325L324 525.5L323 536.5C323 536.9 322.5 541 322.5 543C322.167 543.333 322.3 544.6 321.5 547C320.7 549.4 321.5 552.5 320.5 553.5L322 558L323 562L325 566L328 568H330L334 563L336 557L337 553L338 549L339 543L340 542L342 543L343 545V554.5L341 560L336 571L330 581L325.5 587L325 589V596H334L341 594L346 591L350 588L355 582L358 577L360.5 573L362 567L364 558L365 552L366 543V522Z" },
  { d: "M87 175H184V238H87V175Z" },
  { d: "M184 175H276V238H184V175Z" },
  { d: "M87 238H184V292H87V238Z" },
  { d: "M184 238H276V292H184V238Z" },
  { d: "M35 238C35 231 35.937 223.083 38.5 215.5C41.063 207.917 43.8431 202 50.9375 193.306C55.6733 187.502 61.2955 182.898 67.4831 179.758C73.6707 176.617 80.3026 175 87 175V207V238H35Z" },
  { d: "M34.8 238H79.2028H87V292H29L34.8 238Z" },
  { d: "M89 292H184V354H99.3158L89 292Z" },
  { d: "M29 292H89L75 356H14L29 292Z" },
  { d: "M184 292H276L264 355H184V292Z" },
  { d: "M204 815L200 753.5H276.5L277 815H204Z" },
  { d: "M200 753.5L195 689.5H276L276.5 753.5H200Z" },
  { d: "M195 689.5L186.5 618.5H279.5L276 689.5H195Z" },
  { d: "M186.5 618.5L184 549H277L279.5 618.5H186.5Z" },
  { d: "M86 815V753H163L160.5 815H86Z" },
  { d: "M169 688H85L86 753H163L169 688Z" },
  { d: "M176 616H84L85 688H169L176 616Z" },
  { d: "M184 549H86L84 616H176L184 549Z" },
  { d: "M86.5 549L94 478H184V549H86.5Z" },
  { d: "M270 478H184V549H277L270 478Z" }
];
const BACK_PATHS = [
  { d: "M40 515H1H0V538L1 549L2 556L3 561L6 571L7 574L12 583L18 590L27 595C27 595.667 27.8486 597 31.243 597H41V596L44 593V592L33 579L29 573L24 562L23 557V551L24 547L25 544H26L27 547L28 551L29 554L30 557L32 562L34 567L37 572H42L43 571L44 568L45 564V527L42 520L41 517L40 515Z" },
  { d: "M369 526H324L323 530.5L322 538L321 543.5L320 553L323 563C323.333 566 324.6 572 327 572H331L334 568L337 556L338 546.5L338.5 541H340L345 546.5V553L343.5 560.5L333 579L327 587V589.5C327 591.9 328.333 594.833 329 596L338 595.5L352 587.5L360 577L366 563L368 553L369 546.5V526Z" },
  { d: "M134 48.0001C134 35.5 137.5 18 149.5 9.5C159.382 2.5 173.83 -3.33306e-10 184 0V24V48.0001H157.5H134Z" },
  { d: "M233 48C233 35.5 229.5 18 217.5 9.49998C208.311 0.685766 194.17 -3.33306e-10 184 0V48H233Z" },
  { d: "M92 176H184V239H92V176Z" },
  { d: "M102 358H184V419H102V358Z" },
  { d: "M95 479H184V554H88L95 479Z" },
  { d: "M88 554H184L178 621H86L88 554Z" },
  { d: "M279 554H184L192 621H282L279 554Z" },
  { d: "M86 621H178L171 689H88L86 621Z" },
  { d: "M191 621H282L277 689H198L191 621Z" },
  { d: "M88 689H171L165 756H90L88 689Z" },
  { d: "M198 689H277L278 756H203L198 689Z" },
  { d: "M90 756H165L163 819H88V788L90 756Z" },
  { d: "M203 756H278L280 794V809.5V819H206L203 756Z" },
  { d: "M206 819H280L267 895H219L206 819Z" },
  { d: "M88 819H163L149.824 893H98.1351L88 819Z" },
  { d: "M98 893H149V930.146V963.5V995L143.891 998.841L139.292 1001.38L129.584 1005.46L123.964 1007.49L117.321 1008H111.19L106.591 1007.49L101.993 1005.46L97.3942 1002.4L92.2847 999.35L89.219 994.77L82.5766 986.628L79.5109 981.031L79 974.925L84.6204 970.345L93.8175 959.15L98.4161 944.903L99.9489 929.637L98 893Z" },
  { d: "M291 358H350L359 417H303L291 358Z" },
  { d: "M31 293H90L77 358H17L31 293Z" },
  { d: "M17 358H77L64 418H9L17 358Z" },
  { d: "M267 895H219V931.5V964V995L222 999L226.5 1001.5L236 1005.5L241.5 1007.5L248 1008H254L258.5 1007.5L263 1005.5L267.5 1002.5L272.5 999.5L275.5 995L282 987L285 981.5L285.5 975.5L280 971L271 960L266.5 946L265 931L267 895Z" },
  { d: "M40 239.003C40 231.255 40.5234 211.162 43.4 204.003C46.2766 196.845 45.1048 198.982 50.42 193.503C55.7352 188.025 64.48 182.001 71.5 179.003C79.06 175.776 74.72 176.003 92 176.003V179.503L92 210.503L92 239.003H40Z" },
  { d: "M327 239C327 230.919 327.731 222.466 325.5 215C323.269 207.534 319.122 199.714 315 194C310.878 188.286 302.886 185.092 297.5 182C292.114 178.908 279.83 176 274 176V239H327Z" },
  { d: "M184 479H271L279 554H272.67H229.649H184V479Z" },
  { d: "M134 48H184V85H134V48Z" },
  { d: "M184 48H232.98L234 85H184V48Z" },
  { d: "M134.016 85H184V176H88L112.74 171.5L136.49 159.5L149.849 140.5L148 114.5L141.5 98.5L134.016 85Z" },
  { d: "M184 176H274V239H184V176Z" },
  { d: "M40 239.003H90V293.003H31L40 239.003Z" },
  { d: "M90 239H184V358H101.872L90 293V239Z" },
  { d: "M184 239H278V295L265.872 358H184V239Z" },
  { d: "M278 239H327L336 295H278V239Z" },
  { d: "M278 295H336L350 358H291L278 295Z" },
  { d: "M9 418H64L46 480H3L9 418Z" },
  { d: "M184 358H266L263.976 420H184V358Z" },
  { d: "M101.921 419H184V479H95L101.921 419Z" },
  { d: "M184 420H263.92L271 479H184V420Z" },
  { d: "M303 417H359L363 480H323L303 417Z" },
  { d: "M323 480H363L365 493L366.5 505.5L369 526H324L323 480Z" },
  { d: "M2.97747 480H45.9753L39 515H0L2.97747 480Z" },
  { d: "M184 85H234L223.815 115.5L222.084 138.5L225.891 149L237.804 160.5L254.174 169L275 176H184L184 85Z" }
];

const EnhancedBodyDiagram = ({ currentView, onPainLog, painData = [], externalSelectedZone = null }) => {
  const { t, i18n } = useTranslation();
  const [selectedZone, setSelectedZone] = useState(null);
  const [hoveredZone, setHoveredZone] = useState(null);
  const svgRef = useRef(null);

  // Generate zone maps with translations (regenerate when language changes)
  const [zoneMaps, setZoneMaps] = useState(() => ({
    front: generateZoneMap('front', 48, t),
    back: generateZoneMap('back', 42, t)
  }));

  useEffect(() => {
    // Regenerate zone maps when language changes
    setZoneMaps({
      front: generateZoneMap('front', 48, t),
      back: generateZoneMap('back', 42, t)
    });
  }, [i18n.language, t]);

  const FRONT_ZONE_MAP = zoneMaps.front;
  const BACK_ZONE_MAP = zoneMaps.back;

  // Clear selection when external state is cleared (modal closed)
  useEffect(() => {
    if (externalSelectedZone === null && selectedZone !== null) {
      setSelectedZone(null);
    }
  }, [externalSelectedZone, selectedZone]);

  // Get pain color for a zone based on pain data
  const getPainColor = (zoneId) => {
    const painLog = painData.find(log => log.zoneId === zoneId);
    if (!painLog) return 'transparent'; // No color if no pain data
    
    // If painColor is stored in the log, use it with transparency
    if (painLog.painColor && painLog.painColor.startsWith('#')) {
      // Convert hex to rgba with transparency
      const hex = painLog.painColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, 0.4)`;
    }
    
    // Fallback: map from painLevel if painColor is not available
    let colorHex;
    switch (painLog.painLevel) {
      case 'worst-pain': colorHex = '#ef4444'; break;
      case 'severe': colorHex = '#f97316'; break;
      case 'moderate': colorHex = '#f59e0b'; break;
      case 'mild': colorHex = '#60a5fa'; break;
      case 'no-pain': colorHex = '#10b981'; break;
      default: return 'transparent';
    }
    
    // Convert hex to rgba with transparency
    const hex = colorHex.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, 0.4)`;
  };


  // Handler for clicking a zone
  const handleZoneClick = useCallback((zoneId, zoneName) => {
    setSelectedZone(zoneId);
    if (onPainLog) {
      onPainLog({
        zoneId,
        zoneName,
        view: currentView,
        currentPainLevel: getPainColor(zoneId)
      });
    }
  }, [onPainLog, currentView, getPainColor]);


  return (
    <DiagramContainer>
        <ViewIndicator>
          {currentView === 'front' ? t('bodyDiagram.view.front') : t('bodyDiagram.view.back')}
        </ViewIndicator>
      <SVGContainer view={currentView}>
        <img 
          src={currentView === 'front' ? BodyGridFront : BodyGridBack}
          alt={currentView === 'front' ? t('bodyDiagram.view.front') : t('bodyDiagram.view.back')}
          style={{ 
            width: '100%', 
            height: 'auto'
          }}
        />
        {currentView === 'front' && (
          <ClickOverlay viewBox="0 0 366 1008">
            {FRONT_PATHS.map((pathObj, idx) => {
              const pathKey = `front-${idx + 1}`;
              const zoneId = `front-zone-${idx + 1}`;
              const zoneMapKey = `front-${idx + 1}`;
              const zoneData = FRONT_ZONE_MAP[zoneMapKey];
              const zoneName = zoneData?.name || FRONT_ZONE_NAMES[idx] || `Front Zone ${idx + 1}`;
              const isSelected = selectedZone === zoneId;
              const isHovered = hoveredZone === zoneId;
              const painColor = getPainColor(zoneId);
              
              return (
                <path
                  key={pathKey}
                  d={pathObj.d}
                  fill={painColor}
                  className={isSelected ? 'selected' : ''}
                  onClick={() => handleZoneClick(zoneId, zoneName)}
                  onMouseEnter={() => setHoveredZone(zoneId)}
                  onMouseLeave={() => setHoveredZone(null)}
                />
              );
            })}
          </ClickOverlay>
        )}
        {currentView === 'back' && (
          <ClickOverlay viewBox="0 0 369 1008">
            {BACK_PATHS.map((pathObj, idx) => {
              const pathKey = `back-${idx + 1}`;
              const zoneId = `back-zone-${idx + 1}`;
              const zoneMapKey = `back-${idx + 1}`;
              const zoneData = BACK_ZONE_MAP[zoneMapKey];
              const zoneName = zoneData?.name || BACK_ZONE_NAMES[idx] || `Back Zone ${idx + 1}`;
              const isSelected = selectedZone === zoneId;
              const isHovered = hoveredZone === zoneId;
              const painColor = getPainColor(zoneId);
              
              return (
                <path
                  key={pathKey}
                  d={pathObj.d}
                  fill={painColor}
                  className={isSelected ? 'selected' : ''}
                  onClick={() => handleZoneClick(zoneId, zoneName)}
                  onMouseEnter={() => setHoveredZone(zoneId)}
                  onMouseLeave={() => setHoveredZone(null)}
                />
              );
            })}
          </ClickOverlay>
        )}
      </SVGContainer>
    </DiagramContainer>
  );
};

export default EnhancedBodyDiagram;
